/**
 * The lobby: a round research hall from twenty years ahead (docs/lobby-redesign.md). This
 * module composes the parts in `world/lobby/` and turns the visitor's journey (onboarding
 * step, chosen study, distance to the reception) into light: pathways brighten, pools of
 * light rise, the sculpture gathers energy and the gateway takes the study's colour.
 */
import * as THREE from 'three';
import { TOKENS } from '../ui/theme.ts';
import type { WalkableBounds } from '../xr/Locomotion.ts';
import { LOUNGES, buildLounges, buildParticles } from './lobby/ambient.ts';
import { buildCity } from './lobby/city.ts';
import { buildDisplays, buildMessages } from './lobby/features.ts';
import type { TimeUniform } from './lobby/glow.ts';
import { HALL, buildHall } from './lobby/hall.ts';
import { buildPathways } from './lobby/pathways.ts';
import {
  GATEWAY,
  RECEPTION,
  buildGateway,
  buildReception,
  type Gateway,
  type StationView,
} from './lobby/reception.ts';
import { SCULPTURE, buildSculpture } from './lobby/sculpture.ts';

export { GATEWAY, RECEPTION } from './lobby/reception.ts';

export const LOBBY = {
  radius: HALL.radius,
  height: HALL.height,
  spawn: RECEPTION.spawn,
  lookAt: new THREE.Vector3(0, 1.5, 6),
  onboardingPosition: RECEPTION.card,
  /** Scene background and fog: the blue-hour city outside. */
  background: '#04070b',
  fog: { colour: '#0a1626', near: 70, far: 320 },
};

export type JourneyStep = 'name' | 'role' | 'avatar' | 'done';

export interface Journey {
  step: JourneyStep;
  /** The study chosen in the directory. */
  selected: { code: string; title: string; colour: string } | undefined;
  /** 0 far .. 1 standing at the reception. */
  nearReception: number;
}

export interface LobbyWorld {
  group: THREE.Group;
  bounds: WalkableBounds;
  floors: THREE.Object3D[];
  gateway: Gateway;
  setJourney(journey: Journey): void;
  setStations(views: StationView[]): void;
  update(dt: number): void;
  dispose(): void;
}

export function buildLobby(renderer: THREE.WebGLRenderer): LobbyWorld {
  const time: TimeUniform = { value: 0 };
  const group = new THREE.Group();
  group.name = 'lobby';

  const hall = buildHall(renderer, time);
  group.add(hall.group);
  const sculpture = buildSculpture(time);
  group.add(sculpture.group);
  const pathways = buildPathways(time);
  group.add(pathways.mesh);
  const reception = buildReception();
  group.add(reception.group);
  const gateway = buildGateway(time);
  group.add(gateway.group);
  const displays = buildDisplays(
    time,
    new THREE.Vector3(RECEPTION.spawn.x, 1.6, RECEPTION.spawn.z),
  );
  group.add(displays.group);
  group.add(buildMessages(time, HALL.radius));
  group.add(buildLounges());
  group.add(buildParticles(time, HALL.radius, HALL.height - 1));
  const city = buildCity();
  group.add(city.group);

  // Light: a cool key from above the entrance for people and furniture, a soft fill; the
  // glow everywhere else is emissive.
  const key = new THREE.DirectionalLight('#e6f1ff', 1.6);
  key.position.set(3, 16, 12);
  group.add(key);
  group.add(new THREE.HemisphereLight('#a9c6ee', '#0b0f15', 1.1));

  const walkable: WalkableBounds = {
    minX: -HALL.radius,
    maxX: HALL.radius,
    minZ: -HALL.radius,
    maxZ: HALL.radius,
    circle: { x: 0, z: 0, r: HALL.radius - 1.0 },
    blocked: [
      // The sculpture's dais.
      {
        minX: SCULPTURE.x - SCULPTURE.daisRadius * 0.92,
        maxX: SCULPTURE.x + SCULPTURE.daisRadius * 0.92,
        minZ: SCULPTURE.z - SCULPTURE.daisRadius * 0.92,
        maxZ: SCULPTURE.z + SCULPTURE.daisRadius * 0.92,
      },
      // The lounges.
      ...LOUNGES.map((l) => ({
        minX: l.x - 2.1,
        maxX: l.x + 2.1,
        minZ: l.z - 2.1,
        maxZ: l.z + 2.1,
      })),
      // Behind the gateway.
      { minX: -3.2, maxX: 3.2, minZ: -HALL.radius, maxZ: GATEWAY.zone.minZ - 0.3 },
    ],
  };

  let journey: Journey = { step: 'name', selected: undefined, nearReception: 1 };
  const applyJourney = (): void => {
    const { step, selected, nearReception } = journey;
    const done = step === 'done';
    const stepIndex = ['name', 'role', 'avatar', 'done'].indexOf(step);
    pathways.setLevel('arrival', done ? 0.35 : 0.9);
    pathways.setLevel('reception', done ? 0.45 : 0.55 + stepIndex * 0.15);
    pathways.setLevel('sculpture', done ? (selected ? 0.9 : 0.65) : 0.12);
    pathways.setLevel('gateway', selected ? 1 : done ? 0.5 : 0.08);
    pathways.setLevel('lounges', done ? 0.35 : 0.1);
    pathways.setGatewayColour(selected?.colour);
    sculpture.setEnergy(selected ? 1 : 0.3 + stepIndex * 0.15);
    sculpture.setAccent(selected?.colour);
    gateway.setStudy(selected);
    hall.setPool('reception', TOKENS.cyanSoft, 0.12 + nearReception * (done ? 0.18 : 0.3));
    hall.setPool(
      'sculpture',
      selected?.colour ?? TOKENS.cyan,
      0.2 + (done ? 0.12 : 0) + (selected ? 0.1 : 0),
    );
    hall.setPool('gateway', selected?.colour ?? TOKENS.cyan, selected ? 0.55 : done ? 0.2 : 0.08);
    hall.setPool('loungeEast', TOKENS.cyan, 0.14);
    hall.setPool('loungeWest', TOKENS.cyan, 0.14);
  };
  applyJourney();

  return {
    group,
    bounds: walkable,
    floors: [hall.floor],
    gateway,
    setJourney: (j) => {
      const changed =
        j.step !== journey.step ||
        j.selected?.code !== journey.selected?.code ||
        Math.abs(j.nearReception - journey.nearReception) > 0.02;
      journey = j;
      if (changed) applyJourney();
    },
    setStations: (views) => reception.setStations(views),
    update: (dt) => {
      time.value += dt;
      sculpture.update(dt);
      pathways.update(dt);
      reception.update(dt);
      gateway.update(dt);
      displays.update(dt);
      city.update(dt);
    },
    dispose: () => {
      // Everything the hall built is per entry (the lobby is rebuilt on return).
      hall.dispose();
      const textures = new Set<THREE.Texture>();
      group.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.geometry) return;
        m.geometry.dispose();
        for (const mat of Array.isArray(m.material) ? m.material : [m.material]) {
          if (!mat) continue;
          const anyMat = mat as THREE.Material & {
            map?: THREE.Texture | null;
            uniforms?: Record<string, { value: unknown }>;
          };
          if (anyMat.map) textures.add(anyMat.map);
          for (const u of Object.values(anyMat.uniforms ?? {}))
            if (u.value instanceof THREE.Texture) textures.add(u.value);
          mat.dispose();
        }
      });
      for (const t of textures) t.dispose();
    },
  };
}
